/**
 * The ShopCategory container
 */
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import classNames from "classnames";
import shopTaxonsSelectors from "../../redux/shop/taxons/selectors";
import { push } from "connected-react-router";
import { Link } from "react-router-dom";
import Page from "../../components/Page";
import FourOFour from "../Utils/FourOFour";
import LoadingContent from "../../components/LoadingContent";
import {
  Container,
  Row,
  Col,
  Spinner,
  Button,
  Collapse,
  ListGroup,
  ListGroupItem,
} from "reactstrap";
import { FormGroup, Input, Label } from "reactstrap";
import ShopProductCard from "../../components/ShopProductCard";
import CMSBlock from "../../components/CMSBlock";
import filter from "lodash/filter";
import { MdFilterList } from "react-icons/md";
import { useRegisterEffect, useSsrEffect } from "@issr/core";
import { REACT_APP_SHOP_CDN_SERVER } from "../../utils/env";
import { showTaxonAndProducts } from "../../redux/shop/taxons";
import { listProducts } from "../../redux/shop/products";
import Status from "../Utils/Status";

const _ = {
  filter: filter,
};

/**
 * Frontload function called before component mounts to
 * prefetch the shopCategory we want to show.
 * It looks in the url for an ShopCategory id parameter to load the resource
 */

/**
 * The `show` container for an ShopCategory.
 * Both the component and the connected component are exported for testing purpose
 */
const ShopCategory = (props, context) => {
  const { match } = props;
  let shopTaxonSlug = match
    ? match.params.subslug
      ? match.params.slug + "/" + match.params.subslug
      : match.params.slug
    : false;

  const {
    products,
    shopTaxon,
    availableCmsBlocks,
    shopTaxonLoading,
    lastSlug,
    shopTaxonDictionary,
    showFourOFour,
    errorRedirect,
    productsLoading,
    language,
  } = useSelector((state) => ({
    products: state.shopProducts.list,
    shopTaxon: state.shopTaxons.show,
    availableCmsBlocks: state.cmsblocks.list,
    shopTaxonLoading: state.shopTaxons.listLoading,
    lastSlug: state.shopTaxons.lastSlug,
    shopTaxonDictionary: shopTaxonsSelectors.selectDictionaryForLanguage(state),
    showFourOFour: state.shopTaxons.showFourOFour,
    errorRedirect: state.shopTaxons.errorRedirect,
    productsLoading: state.shopProducts.listLoading,
    language: state.i18nState.lang,
  }));
  if (shopTaxonSlug) shopTaxonSlug = "categories/" + shopTaxonSlug;
  const dispatch = useDispatch();
  const registerEffect = useRegisterEffect();

  const frontload = () => {
    // TODO
    // frontload = load taxon with products
    // -> populate "all" products and filters

    // filter = search products with taxon id
    // onclick = use taxon id for breadcrumb

    // FIXME 404 -> redirect?
    // FIXME reload on language change

    // "categories" needs to be defined in spree
    if (
      ((!shopTaxon && shopTaxonSlug !== lastSlug) ||
        (shopTaxon &&
          shopTaxon.data &&
          shopTaxon.data.attributes &&
          shopTaxon.data.attributes.permalink !== shopTaxonSlug)) &&
      !shopTaxonLoading &&
      !productsLoading
    ) {
      registerEffect(
        dispatch,
        showTaxonAndProducts({
          slug: shopTaxonSlug,
        })
      );
    }
  };
  useSsrEffect(frontload, [shopTaxonSlug, language]);

  const [selectedTaxonomies, setSelectedTaxonomies] = useState([]);
  const [perPage, setPerPage] = useState(40);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const toggleTaxonomyFilter = (taxon, event) => {
    let newTaxonomies = selectedTaxonomies;
    if (event.target.checked) {
      newTaxonomies.push(taxon.id);
    } else {
      newTaxonomies = selectedTaxonomies?.filter((taxonid) => {
        return parseInt(taxonid) !== parseInt(taxon.id);
      });
    }

    setSelectedTaxonomies(newTaxonomies);
    loadTaxonProducts();
  };

  const productList = useSelector((state) => {
    return state.shopProducts.list;
  });

  const loadMoreProducts = () => {
    setPerPage(perPage + 40);
    loadTaxonProducts();
  };
  const loadTaxonProducts = () => {
    const filters = [{ key: "taxons", value: shopTaxon.data.id }];

    if (selectedTaxonomies.length > 0)
      filters.push({
        key: "concat_taxons",
        value: selectedTaxonomies.join(","),
      });

    dispatch(
      listProducts({
        filters: filters,
        perPage: perPage,
        language,
      })
    );
  };

  const toggleMobileFilter = () => {
    setShowMobileFilters(!showMobileFilters);
  };

  /**
   * renders the shopCategory data
   */

  const title =
    shopTaxon && shopTaxon.data && shopTaxon.data.attributes.meta_title
      ? shopTaxon.data.attributes.meta_title
      : shopTaxon && shopTaxon.data && shopTaxon.data.attributes.name
      ? shopTaxon.data.attributes.name
      : "Loading...";
  const keywords =
    shopTaxon && shopTaxon.data && shopTaxon.data.attributes.meta_keywords
      ? shopTaxon.data.attributes.meta_keywords
      : "";
  const description =
    shopTaxon && shopTaxon.data && shopTaxon.data.attributes.meta_description
      ? shopTaxon.data.attributes.meta_description
      : "";
  const published =
    shopTaxon && shopTaxon.data && shopTaxon.data.attributes.updated_at
      ? shopTaxon.data.attributes.updated_at
      : "";

  let parentCategory = false;
  if (
    shopTaxon &&
    shopTaxon.data &&
    shopTaxon.data.relationships.parent &&
    shopTaxon.data.relationships.parent.data &&
    parseInt(shopTaxon.data.relationships.parent.data.id) !== 1
  ) {
    parentCategory = shopTaxon.included.find((obj) => {
      return (
        obj.id === shopTaxon.data.relationships.parent.data.id &&
        obj.type === "taxon"
      );
    });
  }

  let allProductIds = [];
  let loadedProductIds = [];
  let taxonImage = false;
  if (
    shopTaxon &&
    shopTaxon.data &&
    shopTaxon.data.relationships.image &&
    shopTaxon.data.relationships.image.data
  ) {
    taxonImage = shopTaxon.included.find((obj) => {
      return (
        obj.id === shopTaxon.data.relationships.image.data.id &&
        obj.type === "taxon_image"
      );
    });
    // If products loaded, set helper subsets
    allProductIds = shopTaxon.data.relationships.products.data.map(
      (product) => product.id
    );
  }
  const image = taxonImage
    ? REACT_APP_SHOP_CDN_SERVER + taxonImage.attributes.styles[1].url
    : "";

  let taxonomyFilters = [];
  let subTaxonomyFilter = [];

  if (products && products.included) {
    // If products loaded, set helper subsets
    if (products.data) {
      loadedProductIds = products.data.map((product) => product.id);
    }
    // exclude taxon id 1 since it's the "general category"
    if (products.included) {
      taxonomyFilters = products.included.filter((obj) => {
        return (
          obj.type === "taxon" &&
          parseInt(obj.id) !== 1 &&
          obj.id !== shopTaxon.data.id &&
          !obj.relationships.parent.data
        );
      });
      subTaxonomyFilter = products.included.filter((obj) => {
        return (
          obj.type === "taxon" &&
          parseInt(obj.id) !== 1 &&
          obj.relationships.parent.data &&
          obj.relationships.parent.data.id === shopTaxon.data.id
        );
      });
    }
  }

  Object.keys(shopTaxonDictionary).forEach((taxonId) => {
    let taxon = shopTaxonDictionary[taxonId];
    if (
      taxon.attributes.is_root &&
      !taxon.relationships.parent.data &&
      taxon.relationships.children.data
    ) {
      if (
        taxonomyFilters.find &&
        !taxonomyFilters.find(
          (txFilter) => parseInt(txFilter.id) === parseInt(taxon.id)
        )
      ) {
        taxonomyFilters.push(taxon);
      }
    }
  });
  taxonomyFilters.sort((a, b) => {
    if (parseInt(a.id) < parseInt(b.id)) return -1;
    if (parseInt(a.id) > parseInt(b.id)) return 1;
    return 0;
  });

  const dataLayer = !productsLoading
    ? [
        {
          event: "view_item_list",
          value: productList?.data
            ?.filter((obj) =>
              shopTaxon?.data?.relationships?.products?.data
                ?.map((tp) => tp.id)
                .includes(obj.id)
            )
            .reduce((sum, product) => {
              return sum + (parseFloat(product.attributes.price) || 0);
            }, 0),
          items: productList?.data
            ?.filter((obj) =>
              shopTaxon?.data?.relationships?.products?.data
                ?.map((tp) => tp.id)
                .includes(obj.id)
            )
            .map((product, index) => {
              return {
                name: product.attributes.name,
                id: product.attributes.sku || product.attributes.slug, // TODO We need th master sku!
                price: product.attributes.price,
                brand: "myfolie",
                category: product.attributes.product_type,
                google_business_vertical: "retail",
              };
            }),
        },
        {
          ecommerce: {
            impressions: productList?.data
              ?.filter((obj) =>
                shopTaxon?.data?.relationships?.products?.data
                  ?.map((tp) => tp.id)
                  .includes(obj.id)
              )
              .map((product, index) => {
                return {
                  name: product.attributes.name,
                  id: product.attributes.sku || product.attributes.slug, // TODO We need th master sku!
                  price: product.attributes.price,
                  brand: "myfolie",
                  category: product.attributes.product_type,
                  list: "Category Page",
                };
              }),
          },
        },
      ]
    : null;

  return showFourOFour ? (
    <Status code="404" redirect={errorRedirect}>
      <FourOFour />
    </Status>
  ) : (
    <Page
      id="shopcategory"
      className="shopcategory"
      title={title}
      image={image}
      published={published}
      description={description}
      keywords={keywords}
      lang={language}
      dataLayer={dataLayer}
      pageType="category"
    >
      {shopTaxonLoading && <LoadingContent layout="category" />}

      {!shopTaxonLoading && shopTaxon && shopTaxon.data && (
        <Container>
          <Row
            className="bg-light"
            style={
              taxonImage
                ? {
                    height: "180px",
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "cover",
                    backgroundImage: `url('${
                      REACT_APP_SHOP_CDN_SERVER +
                      taxonImage.attributes.styles[2].url
                    }')`,
                  }
                : { height: "140px" }
            }
          >
            <Col>
              <h1 className="mt-4">{shopTaxon.data.attributes.name}</h1>
            </Col>
          </Row>

          {shopTaxon.data.attributes.description && (
            <p>{shopTaxon.data.attributes.description}</p>
          )}
        </Container>
      )}

      {!shopTaxonLoading && shopTaxon && shopTaxon.data && (
        <Container>
          {products.meta && (
            <Row>
              <Col>
                <Button
                  color="primary"
                  onClick={toggleMobileFilter}
                  className="shopcategory__mobilefilterbutton d-none mb-2"
                  outline
                >
                  <MdFilterList className="mr-2" />
                  {context.t("Show Filters")}
                </Button>
              </Col>
              <Col className="text-right">
                {productsLoading && (
                  <span className="mr-2 small text-secondary">
                    {context.t("loading products...")}
                    <Spinner size="sm" />
                  </span>
                )}
                {products.meta.total_count} {context.t("items")}
              </Col>
            </Row>
          )}
          <Row>
            <Col
              lg={3}
              md={12}
              className={classNames("shopcategory__filterbar", {
                "shopcategory__filterbar--modalopen": showMobileFilters,
              })}
            >
              <ListGroup>
                {shopTaxon.data.relationships &&
                  (shopTaxon.data.relationships.children || parentCategory) && (
                    <ListGroupItem>
                      <div className="h4">
                        {context.t("related categories")}
                      </div>
                      <ul>
                        <li>
                          <Link to={`/${language}/shop/`}>
                            {context.t("All Categories")}
                          </Link>
                        </li>
                        {parentCategory && (
                          <li>
                            <Link
                              to={`/${language}/c/${parentCategory.attributes.permalink.replace(
                                "categories/",
                                ""
                              )}/`}
                            >
                              &laquo; {parentCategory.attributes.name}
                            </Link>
                          </li>
                        )}
                        {shopTaxon.data.relationships.children.data.map(
                          (child) => {
                            const subcat = shopTaxon.included.find((obj) => {
                              return (
                                obj.id === child.id && obj.type === "taxon"
                              );
                            });
                            if (
                              subcat.relationships &&
                              subcat.relationships.products &&
                              subcat.relationships.products.data &&
                              subcat.relationships.products.data.length > 0
                            ) {
                              return (
                                <li key={child.id}>
                                  <Link
                                    to={`/${language}/c/${subcat.attributes.permalink.replace(
                                      "categories/",
                                      ""
                                    )}/`}
                                  >
                                    {subcat.attributes.name}
                                  </Link>
                                </li>
                              );
                            } else {
                              return "";
                            }
                          }
                        )}
                      </ul>
                    </ListGroupItem>
                  )}

                {taxonomyFilters &&
                  taxonomyFilters.map((maintaxon) => (
                    <ListGroupItem key={maintaxon.attributes.name}>
                      <div className="h4">{maintaxon.attributes.name}</div>
                      <Collapse isOpen={true}>
                        {maintaxon &&
                          maintaxon.relationships.children.data.map(
                            (subtaxon, idx) => {
                              const dictionaryTaxon =
                                shopTaxonDictionary[subtaxon.id];
                              let childtaxon = products.included
                                ? products.included.find((obj) => {
                                    return (
                                      obj.type === "taxon" &&
                                      obj.id === subtaxon.id
                                    );
                                  })
                                : false;
                              let isDisabled =
                                shopTaxonLoading || productsLoading;
                              if (dictionaryTaxon && !childtaxon) {
                                childtaxon = dictionaryTaxon;
                              }
                              const productCount = childtaxon
                                ? childtaxon.relationships.products.data.filter(
                                    (obj) => {
                                      return (
                                        obj.type === "product" &&
                                        loadedProductIds.includes(obj.id)
                                      );
                                    }
                                  ).length
                                : 0;
                              const allProductCount = childtaxon
                                ? childtaxon.relationships.products.data.filter(
                                    (obj) => {
                                      return (
                                        obj.type === "product" &&
                                        allProductIds.includes(obj.id)
                                      );
                                    }
                                  ).length
                                : 0;
                              return childtaxon ? (
                                <FormGroup key={"subtaxon-" + idx} check>
                                  <Input
                                    type="checkbox"
                                    disabled={isDisabled}
                                    name={`taxon-${childtaxon.id}`}
                                    id={`taxon-${childtaxon.id}`}
                                    checked={selectedTaxonomies?.find((obj) => {
                                      return obj === childtaxon.id;
                                    })}
                                    onChange={toggleTaxonomyFilter.bind(
                                      this,
                                      subtaxon
                                    )}
                                  />
                                  <Label check for={`taxon-${childtaxon.id}`}>
                                    {childtaxon.attributes.name} ({productCount}{" "}
                                    / {allProductCount})
                                  </Label>
                                </FormGroup>
                              ) : (
                                ""
                              );
                            }
                          )}
                      </Collapse>
                    </ListGroupItem>
                  ))}

                {subTaxonomyFilter &&
                  subTaxonomyFilter.map(
                    (maintaxon, idx) =>
                      maintaxon &&
                      maintaxon.relationships.children.data.length > 0 && (
                        <ListGroupItem key={"maintaxon-" + idx}>
                          <div className="h4">{maintaxon.attributes.name}</div>
                          <Collapse isOpen={true}>
                            {maintaxon.relationships.children.data.map(
                              (subtaxon, index) => {
                                const childtaxon = products.included.find(
                                  (obj) => {
                                    return (
                                      obj.type === "taxon" &&
                                      obj.id === subtaxon.id
                                    );
                                  }
                                );
                                const productCount = childtaxon
                                  ? childtaxon.relationships.products.data.filter(
                                      (obj) => {
                                        return (
                                          obj.type === "product" &&
                                          loadedProductIds.includes(obj.id)
                                        );
                                      }
                                    ).length
                                  : 0;
                                const allProductCount = childtaxon
                                  ? childtaxon.relationships.products.data.filter(
                                      (obj) => {
                                        return (
                                          obj.type === "product" &&
                                          allProductIds.includes(obj.id)
                                        );
                                      }
                                    ).length
                                  : 0;
                                return childtaxon ? (
                                  <FormGroup
                                    key={"subtaxon-formgroup-" + index}
                                    check
                                  >
                                    <Input
                                      type="checkbox"
                                      name={`taxon-${childtaxon.id}`}
                                      id={`taxon-${childtaxon.id}`}
                                      checked={selectedTaxonomies?.find(
                                        (obj) => {
                                          return obj === childtaxon.id;
                                        }
                                      )}
                                      onChange={toggleTaxonomyFilter.bind(
                                        this,
                                        subtaxon
                                      )}
                                    />
                                    <Label for={`taxon-${childtaxon.id}`}>
                                      {childtaxon.attributes.name} (
                                      {productCount} / {allProductCount})
                                    </Label>
                                  </FormGroup>
                                ) : (
                                  ""
                                );
                              }
                            )}
                          </Collapse>
                        </ListGroupItem>
                      )
                  )}
              </ListGroup>
              {productsLoading && (
                <span className="mt-3 small text-secondary">
                  <Spinner size="sm" />{" "}
                  {context.t("updating available filters...")}
                </span>
              )}
              <div className="shopcategory__mobilefilterclose d-none mt-3">
                <Button color="primary" onClick={toggleMobileFilter} block>
                  {context.t("Hide Filters")}
                </Button>
              </div>
            </Col>

            <Col lg={9} md={12} className="shopcategory__products">
              <Row>
                {products.data &&
                  products.data.map((product, idx) => {
                    // FIXME what if image is not set?
                    let newProduct = { ...product };

                    if (
                      product.relationships &&
                      product.relationships.images.data.length > 0
                    ) {
                      if (products.included) {
                        newProduct.thumbnail = {
                          img_url: products.included.filter((incl) => {
                            return (
                              incl.type === "image" &&
                              incl.id ===
                                product?.relationships?.images?.data[0]?.id
                            );
                          })[0]?.attributes?.styles[2]?.url,
                        };
                      }
                    }
                    return (
                      <Col
                        key={`product-${idx}`}
                        lg={3}
                        md={4}
                        xs={6}
                        className="mb-3"
                      >
                        <ShopProductCard
                          product={newProduct}
                          language={language}
                          navigateTo={(url) => dispatch(push(url))}
                          titleTag="h2"
                        />
                      </Col>
                    );
                  })}
                {products.data && products.data.length === 0 && (
                  <Col className="d-flex flex-column justify-content-center align-items-center">
                    <p className="my-5 text-center">
                      {context.t("No Products found")}
                      <br />
                      {context.t("Please Change Filters or Category")}
                    </p>
                  </Col>
                )}
                {productsLoading && (
                  <>
                    <Col lg={3} md={4} xs={6}>
                      <div className="sk-loading sk-b30 bg-light" />
                    </Col>
                    <Col lg={3} md={4} xs={6}>
                      <div className="sk-loading sk-b30 bg-light" />
                    </Col>
                    <Col lg={3} md={4} xs={6}>
                      <div className="sk-loading sk-b30 bg-light" />
                    </Col>
                    <Col lg={3} md={4} xs={6}>
                      <div className="sk-loading sk-b30 bg-light" />
                    </Col>
                  </>
                )}
              </Row>
              {products &&
                products.data &&
                !productsLoading &&
                products.meta.total_count > products.meta.count && (
                  <Button
                    block
                    onClick={loadMoreProducts}
                    outline
                    className="mt-5"
                  >
                    {context.t("load more")}
                  </Button>
                )}
            </Col>
          </Row>

          {shopTaxon.data.attributes.cms_blocks &&
            shopTaxon.data.attributes.cms_blocks
              .split(",")
              .map((cms_block, idx) => {
                const cms_block_data = _.filter(
                  availableCmsBlocks,
                  function (cmsBlock) {
                    return cmsBlock.slug === cms_block;
                  }
                );

                return cms_block_data && cms_block_data[0] ? (
                  <Container key={"cmsblock-" + idx} className="my-5">
                    <CMSBlock cmsBlock={cms_block_data[0]} />
                  </Container>
                ) : (
                  ""
                );
              })}
        </Container>
      )}
    </Page>
  );
};

/**  define proptype for the 'translation' function  */
ShopCategory.contextTypes = {
  t: PropTypes.func,
};

/** export the redux connected component and add a frontload call */
export default ShopCategory;
